import React, { FC, useEffect, useState, useContext } from 'react';
import styled, { css } from 'styled-components';
import { graphql, navigate } from 'gatsby';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { Typography } from '../../../Typography';
import { Button } from '../../../Button';
import { Panel } from '../../../Panel';
import { Search } from '../../../icon/icons/Search';
import { UseLocationAutoComplete } from '../../../../hooks/useLocationAutoComplete';
import { useGet_LocationLazyQuery } from '../../../../types/graphqlHelpers';
import Blade from '../../../Blade';
import { UiStateContext } from '../../../../context/UiStateContext';
import { IconLink } from '../../../IconLink';
import { StyledLinkList } from '../../../LinkList';
import { Spacer } from '../../../Spacer';
import { AutoComplete } from '../../../forms/Autocomplete';
import { createMarkup } from '../../../../utils';
import { useDataLayerPush } from '../../../../hooks/useDataLayerPush';

export interface LocationSearchHeroProps {
  title: string;
  lead: string;
}

const StyledLocationSearchHero = styled.div`
  ${({ theme: { space, media } }) => css`
    display: flex;
    flex-direction: column;
    gap: ${space.md};
    @media (min-width: ${media.xl}) {
      flex-direction: row;
      justify-content: space-between;
    }
  `};
`;

const StyledForm = styled.form`
  ${({ theme: { space, media } }) => css`
    display: flex;
    flex-direction: column;
    gap: ${space.md};
    @media (min-width: ${media.xl}) {
      flex-direction: row;
      gap: ${space.sm};

      > *:first-child {
        min-width: 350px;
      }
    }
  `};
`;

export const LocationSearchHero: FC<LocationSearchHeroProps> = ({ title, lead, ...rest }) => {
  const { autoCompleteSuggestions, handleAutoComplete, loading: autoCompleteLoading } = UseLocationAutoComplete();
  const [getLocation, { loading, error, data: locationData }] = useGet_LocationLazyQuery();
  const [activePartialPostcodeAreas, setActivePartialPostcodeAreas] = useState();
  const { setActiveBlade } = useContext(UiStateContext);
  const [searchedLocation, setSearchedLocation] = useState<string>();
  const [invalidLocation, setInvalidLocation] = useState<boolean>(false);
  const { dataLayerPush } = useDataLayerPush();

  const formMethods = useForm({
    mode: 'onSubmit',
  });

  const { handleSubmit, control, formState } = formMethods;
  const { isSubmitted } = formState;

  // Form action

  const formAction = () => {
    const results = locationData?.location?.areaGuideFinder;

    // If we have data(found a branch) and its not loading or state of error
    if (results && results.length > 0 && (!error || !loading)) {
      setInvalidLocation(false);
      // Check if first result has areas (we then know its a partial postcode)
      if (results[0].areas && results[0].areas) {
        // Set the areas and open the blade
        setActivePartialPostcodeAreas(results[0].areas);
        setActiveBlade('locationFinderAreas');
        dataLayerPush({
          event: 'areaInformationSearch',
          areaInformationSearchTerm: searchedLocation,
          areaInformationSearchLocation: 'multiple london areas covered',
          formValidation: 'success',
          formError: undefined,
        });
      } else {
        // Navigate to first result
        navigate(results[0].relativeUrl);
        dataLayerPush({
          event: 'areaInformationSearch',
          areaInformationSearchTerm: searchedLocation,
          areaInformationSearchLocation: results[0].name,
          formValidation: 'success',
          formError: undefined,
        });
      }
    } else {
      // No results
      setInvalidLocation(true);
    }
  };

  // Handle data change

  useEffect(() => {
    // Run the form action if the location data changes
    formAction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationData]);

  const onSubmit = (formData) => {
    const { location } = formData;

    // Set search location (used for blade)

    setSearchedLocation(location);

    // get location

    getLocation({ variables: { location } });

    // Check if current Search location is the same as form input value then run the form action

    if (location === searchedLocation) {
      formAction();
    }
  };

  return (
    <>
      <Panel backgroundColor="white" shadow {...rest}>
        <StyledLocationSearchHero>
          {/* Header */}
          <div>
            <Typography component="h2" variant="titleMedium">
              {title}
            </Typography>
            <Typography component="p" variant="lead">
              {createMarkup(lead)}
            </Typography>
          </div>
          {/* Form */}
          <FormProvider {...formMethods}>
            <StyledForm onSubmit={handleSubmit(onSubmit)} method="post" noValidate>
              <Controller
                name="location"
                control={control}
                defaultValue=""
                render={({ field: { onChange } }) => (
                  <AutoComplete
                    suggestions={autoCompleteSuggestions}
                    handleAutoComplete={handleAutoComplete}
                    handleSelectValue={(newValue) => onChange(newValue)}
                    loading={autoCompleteLoading}
                    setValueOnChange
                    placeholder="Search by location, postcode"
                    errorMessage={
                      invalidLocation && isSubmitted && !loading ? <p>Enter a valid location. E.g. Acton or W3</p> : ''
                    }
                  />
                )}
              />
              <Button label="Search" iconRight={<Search />} type="submit" disableClickTracking />
            </StyledForm>
          </FormProvider>
        </StyledLocationSearchHero>
      </Panel>

      {/* Location Finder Blade */}

      <Blade id="locationFinderAreas">
        <Spacer>
          <Typography component="h2" variant="displaySmall">
            {searchedLocation} covers multiple London areas
          </Typography>
          <p>Please select which area you would like to view the area guide for:</p>
          <StyledLinkList>
            {activePartialPostcodeAreas?.map(({ name, relativeUrl }) => (
              <li key={name}>
                <IconLink arrowLeft label={name} url={relativeUrl} />
              </li>
            ))}
          </StyledLinkList>
        </Spacer>
      </Blade>
    </>
  );
};

export const LocationSearchHeroQuery = graphql`
  fragment LocationSearchHero on KFH_UmbracoLocationSearchHeroComponentType {
    title
    lead
  }
`;
